/**
 * The contents of this file are subject to the license and copyright
 * detailed in the LICENSE_ATMIRE and NOTICE_ATMIRE files at the root of the source
 * tree and available online at
 *
 * https://www.atmire.com/software-license/
 */
import { Component, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AtmireSearchResultListElementComponent } from '../../../../../../../../app-atmire/shared/object-list/search-result-list-element/atmire-search-result-list-element.component';
import { Context } from '../../../../../../../../app/core/shared/context.model';
import { ViewMode } from '../../../../../../../../app/core/shared/view-mode.model';
import { listableObjectComponent } from '../../../../../../../../app/shared/object-collection/shared/listable-object/listable-object.decorator';
import { TruncatableService } from '../../../../../../../../app/shared/truncatable/truncatable.service';
import { RelationshipService } from '../../../../../../../../app/core/data/relationship.service';
import { NotificationsService } from '../../../../../../../../app/shared/notifications/notifications.service';
import { ItemDataService } from '../../../../../../../../app/core/data/item-data.service';
import { BitstreamDataService } from '../../../../../../../../app/core/data/bitstream-data.service';
import { DSONameService } from '../../../../../../../../app/core/breadcrumbs/dso-name.service';
import { SelectableListService } from '../../../../../../../../app/shared/object-list/selectable-list/selectable-list.service';
import { MetadataValue } from '../../../../../../../../app/core/shared/metadata.models';
import { NameVariantModalComponent } from '../../../../../../../../app/entity-groups/research-entities/submission/name-variant-modal/name-variant-modal.component';
import { Item } from '../../../../../../../../app/core/shared/item.model';
import { ItemSearchResult } from '../../../../../../../../app/shared/object-collection/shared/item-search-result.model';
import { MultilingualMetadataService } from '../../../../../../../../app-atmire/core/shared/multilingual-metadata.service';

@listableObjectComponent('PersonSearchResult', ViewMode.ListElement, Context.EntitySearchModalWithNameVariants, 'atmire')
@Component({
  selector: 'ds-person-search-result-list-submission-element',
  styleUrls: ['../../../../../../../../app/entity-groups/research-entities/submission/item-list-elements/person/person-search-result-list-submission-element.component.scss'],
  templateUrl: '../../../../../../../../app/entity-groups/research-entities/submission/item-list-elements/person/person-search-result-list-submission-element.component.html'
})

/**
 * The component for displaying a list element for an item search result of the type Person
 */
export class PersonSearchResultListSubmissionElementComponent extends AtmireSearchResultListElementComponent<ItemSearchResult, Item> implements OnInit {
  allSuggestions: string[];
  selectedName: string;
  alternativeField = 'dc.title.alternative';

  constructor(protected truncatableService: TruncatableService,
              private relationshipService: RelationshipService,
              private notificationsService: NotificationsService,
              private translateService: TranslateService,
              private modalService: NgbModal,
              private itemDataService: ItemDataService,
              private bitstreamDataService: BitstreamDataService,
              private selectableListService: SelectableListService,
              protected dsoNameService: DSONameService,
              protected multilingualMetadataService: MultilingualMetadataService,
  ) {
    super(truncatableService, dsoNameService, multilingualMetadataService);
  }

  ngOnInit() {
    super.ngOnInit();
    const defaultValue = this.firstMetadataValue('person.familyName') + ', ' + this.firstMetadataValue('person.givenName');
    const alternatives = this.allMetadataValues(this.alternativeField);
    this.allSuggestions = [defaultValue, ...alternatives];

    this.relationshipService.getNameVariant(this.listID, this.dso.uuid)
      .pipe(take(1))
      .subscribe((nameVariant: string) => {
        this.selectedName = nameVariant || defaultValue;
        }
      );
  }

  select(value) {
    this.relationshipService.setNameVariant(this.listID, this.dso.uuid, value);
    this.selectableListService.isObjectSelected(this.listID, this.object)
      .pipe(take(1))
      .subscribe((selected) => {
        if (!selected) {
          this.selectableListService.selectSingle(this.listID, this.object);
        }
      });
  }

  selectCustom(value) {
    if (!this.allSuggestions.includes(value)) {
      this.openModal(value)
        .then(() => {
          // user clicked ok: store the name variant in the item
            const newName: MetadataValue = new MetadataValue();
            newName.value = value;

            const existingNames: MetadataValue[] = this.dso.metadata[this.alternativeField] || [];
            const alternativeNames = { [this.alternativeField]: [...existingNames, newName] };
            const updatedItem =
              Object.assign({}, this.dso, {
                metadata: {
                  ...this.dso.metadata,
                  ...alternativeNames
                },
              });
            this.itemDataService.update(updatedItem).pipe(take(1)).subscribe();
            this.itemDataService.commitUpdates();
      }).catch(() => {
        // user clicked cancel: use the name variant only for this relation, no further action required
      }).finally(() => {
        this.select(value);
      });
    }
  }

  openModal(value): Promise<any> {
    const modalRef = this.modalService.open(NameVariantModalComponent, { centered: true });

    const modalComp = modalRef.componentInstance;
    modalComp.value = value;
    return modalRef.result;
  }
}
